import dayjs from 'dayjs';
import {Spin} from '@baidu/qianfan-antd-kit';
import {Cookies} from 'react-cookie';
import {getImageInfo, getParamStr} from '@/utils/util';
import {
    BusinessOcr,
    CompRemitQueryParams,
    CompRemitQueryResult,
    CompRemitSubmit,
    CompRemitSubmitResult,
    LegalAuthorizedResult,
    LegalFaceResult,
    CompLegalLivingQueryParams,
    CompAuthLegalQueryParams,
    CompAuthLegalQueryResult,
    CompLegalLivingQueryResult,
    CompAuthLegalSubmitParams,
    CompLegalLivingSubmitParams,
    CompLegalSubmitResult,
    CompRechargeQueryParams,
    CompRechargeQueryResult,
    CompRechargeSubmitParams,
    CompRechargeSubmitResult,
    CompRechargeCreateParams,
    CompRemitApply,
    CompLicenseQueryResult,
    CompRegisterParams,
    CompTwoFactorParams,
    CompTwoFactorResult,
    CompChangeQuery,
    CompRegisterResult,
} from '@/types/api/company';
import {Modal, message} from '@/utils/escapeAntd';
import {QUALIFY_CHANGE_STATE, TIME_FORMATE, TIME_FORMATE_SECOND} from '@/dicts/qualify';
import {FaceToken, PassInfo, PipelineType, QualifyInfo} from '@/types/api/qualify';
import {
    ChangeInfo,
    ChangeLog,
    CompChangeOriginPersonResult,
    CompChangeOriginPersonSubmit,
    CompChangeOriginSubmitParams,
    CompanyChangeCheck,
    CompanyChangeOriginResult,
} from '@/types/api/change';
import {HttpConfig} from '@/types/http';
import {AuthWay, AuthWayParams} from '@/types/api/list';
import {
    FaceLog,
    PersonBankQueryParams,
    PersonBankQueryResult,
    PersonBankSubmit,
    PersonDocQueryParams,
    PersonDocQueryResult,
    PersonDocSubmit,
    PersonFaceQueryParams,
    PersonFaceQueryResult,
    PersonFaceSubmit,
} from '@/types/api/person';
import {FaceQuery, GetShortUrlParams} from '@/types/params';
import {AuthRecordItem} from '@/types/api/authRecord';
import {cookies} from '@/modules/App';
import {LicenseInfo, LicenseInfoParams, LicenseUpdateParams} from '@/types/api/license';
import ErrorContent from '@/components/ErrorBoundary/ErrorContent';
import {changeInstanceStatusCheck} from '@/modules/Change/components/changeUtil';
import CountdownText from '@/components/CountdownButton/CountdownText';
import {isLoadByIframe} from '@/utils/util';
import {API} from './api';
import {Http} from './http';

let loadingCount = 0;
const TARGET_DETAIL = '请登录主账号完成实名认证';

const QualifyHttpConfig = {
    headers: {
        csrftoken: new Cookies().get('bce-user-info') || '',
        'X-Request-By': 'ERApplication',
    },
    errorHandler: (error: any) => {
        const errMessage = error?.message;
        const skipRedirect = errMessage?.detail === TARGET_DETAIL;
        // 特殊兜底：若为未实名子用户账户，则会被重定向智能云概述页面，重定向后页面可以再次唤起实名弹窗；在弹窗场景中，会造成多个弹窗嵌套问题，所以该场景取消重定向逻辑
        if (errMessage?.redirect && skipRedirect && isLoadByIframe()) {
            message.info({
                key: errMessage.redirect,
                content: TARGET_DETAIL,
            });
            return;
        }
        if (errMessage?.redirect) {
            if (errMessage?.detail) {
                return message
                    .info({
                        key: errMessage.redirect,
                        content: (
                            <CountdownText
                                countdownLimit={3}
                                invoke
                                countdownLabel={(time: number) => `${errMessage?.detail}，${time}s后自动跳转`}
                                label="即将跳转"
                            />
                        ),
                        duration: 3,
                    })
                    .then(() => (window.location.href = errMessage.redirect));
            }
            window.location.href = errMessage.redirect;
            return;
        }
        Modal.error({
            title: '请求异常！',
            autoFocusButton: null,
            content: <ErrorContent message={error?.message} />,
        });
    },
    loadingHandler: {
        create: () => {
            if (loadingCount === 0) {
                message.loading({
                    icon: <Spin />,
                    content: '  加载中...',
                    duration: 0,
                });
            }
            loadingCount++;
        },
        clear: () => {
            loadingCount--;
            if (loadingCount <= 0) {
                message.destroy();
                loadingCount = 0;
            }
        },
    },
};

class QualifyClient extends Http {
    // eslint-disable-next-line @typescript-eslint/no-useless-constructor
    constructor(config: HttpConfig) {
        super(config);
    }

    qualifyInfo(config?: HttpConfig) {
        return this.post<QualifyInfo>(API.qualifyInfo, undefined, config);
    }

    getAuthWay(params: AuthWayParams, config?: HttpConfig) {
        return this.post<AuthWay>(API.availableAuth, params, config);
    }

    upload(file: File) {
        const {lastModifiedDate, size, type, name, lastModified} = file as any;
        const params = {
            id: `WU_FILE_${lastModified || new Date()}`,
            name,
            type,
            lastModifiedDate: lastModifiedDate || new Date(),
            size,
        };
        const data = new Blob([file]);
        const url = getParamStr(API.upload, params);
        const csrfToken = cookies.get('bce-user-info') || '';
        return this.post<{
            id: string;
            filename: string;
        }>(url, data, {
            headers: {
                Accept: 'image/avif,image/webp,image/apng,image/svg+xml,image/*,*/*;q=0.8',
                'Content-Type': type,
                'File-Name': encodeURI(name),
                csrftoken: csrfToken.replace(/"/g, ''),
            },
        });
    }

    businessOcr(filename: string, config?: HttpConfig) {
        return this.get<BusinessOcr>(API.businessOcr, {filename}, config);
    }

    compBankList(config?: HttpConfig) {
        return this.post<string[]>(API.compBankList, undefined, config);
    }

    queryCompTransfer(params: CompRemitQueryParams, config?: HttpConfig) {
        return this.post<CompRemitQueryResult>(API.queryCompTransfer, params, config);
    }

    submitCompTransfer(data: CompRemitSubmit, config?: HttpConfig) {
        return this.post<CompRemitSubmitResult>(API.submitCompTransfer, data, config);
    }

    applyCompTransfer(data: CompRemitApply, config?: HttpConfig) {
        return this.post<CompRemitSubmitResult>(API.applyCompTransfer, data, config);
    }

    remitFaceQuery(data: CompRemitApply, config?: HttpConfig) {
        return this.post(API.remitFaceQuery, data, config);
    }

    remitFaceToken(config?: HttpConfig) {
        return this.post(API.remitFaceToken, null, {
            headers: {
                'Content-Type': 'application/json',
            },
            ...config,
        });
    }

    verifyCompTransfer(
        data: {
            transferAmount: string;
            qualifyChangeState: QUALIFY_CHANGE_STATE;
        },
        config?: HttpConfig
    ) {
        return this.post<CompRemitQueryResult>(API.verifyCompTransfer, data, config);
    }

    queryPersonFace(params: PersonFaceQueryParams, config?: HttpConfig) {
        return this.post<PersonFaceQueryResult>(API.queryPersonFace, params, config);
    }

    personFaceSubmit(params: PersonFaceSubmit, config?: HttpConfig) {
        return this.post<{
            verifyToken: string;
        }>(API.personFaceSubmit, params, config);
    }

    passInfo(config?: HttpConfig) {
        return this.post<PassInfo>(API.passInfo, undefined, config);
    }

    getPersonFaceH5(params: FaceQuery, config?: HttpConfig): Promise<PersonFaceQueryResult> {
        return this.post(API.queryPersonFace, params, config);
    }

    getLegalFaceH5(params: FaceQuery, config?: HttpConfig): Promise<LegalFaceResult> {
        return this.post(API.legalLivingResult, params, config);
    }

    getLegalAuthorizedH5(params: FaceQuery, config?: HttpConfig): Promise<LegalAuthorizedResult> {
        return this.post(API.legalAuthorizedLivingResult, params, config);
    }

    changeInfo(config?: HttpConfig): Promise<ChangeInfo> {
        return this.post(API.changeInfo, undefined, config).then((change: any) => {
            change.status = changeInstanceStatusCheck(change);
            const originPassTime = change?.originalMainBody?.passTime;
            const newPassTime = change?.newMainBody?.passTime;
            if (originPassTime) {
                change.originalMainBody.passTime = dayjs(originPassTime).format(TIME_FORMATE);
            }
            if (newPassTime) {
                change.newMainBody.passTime = dayjs(newPassTime).format(TIME_FORMATE);
            }
            if (change?.originalMainBody?.applyTime) {
                change.originalMainBody.applyTime = dayjs(change?.originalMainBody?.applyTime)
                    .format(TIME_FORMATE_SECOND);
            }
            if (change?.newMainBody?.applyTime) {
                change.newMainBody.applyTime = dayjs(change.newMainBody.applyTime).format(TIME_FORMATE_SECOND);
            }
            return change as ChangeInfo;
        });
    }

    queryPerBank(params: PersonBankQueryParams, config?: HttpConfig) {
        return this.post<PersonBankQueryResult>(API.queryPerBank, params, config);
    }

    sendPerBankAuthCode(mobile: string, config?: HttpConfig) {
        return this.post(API.sendPerBankAuthCode, {mobile}, config);
    }

    submitPerBank(params: PersonBankSubmit, config?: HttpConfig) {
        return this.post(API.submitPerBank, params, config);
    }

    legalLivingResult(params: CompLegalLivingQueryParams, config?: HttpConfig) {
        return this.post<CompLegalLivingQueryResult>(API.legalLivingResult, params, config);
    }

    legalLivingSubmit(params: CompLegalLivingSubmitParams, config?: HttpConfig) {
        return this.post<CompLegalSubmitResult>(API.legalLivingSubmit, params, config);
    }

    legalAuthorizedLivingResult(params: CompAuthLegalQueryParams, config?: HttpConfig) {
        return this.post<CompAuthLegalQueryResult>(API.legalAuthorizedLivingResult, params, config);
    }

    enableAuthorizedPerson(config?: HttpConfig) {
        return this.get<boolean>(API.enableAuthorizedPerson, null, config);
    }

    legalAuthorizedLivingSubmit(params: CompAuthLegalSubmitParams, config?: HttpConfig) {
        return this.post<CompLegalSubmitResult>(API.legalAuthorizedLivingSubmit, params, config);
    }

    queryCompPayment(params: CompRechargeQueryParams, config?: HttpConfig) {
        return this.post<CompRechargeQueryResult>(API.queryCompPayment, params, config);
    }

    submitPayment(params: CompRechargeSubmitParams, config?: HttpConfig) {
        return this.post<CompRechargeSubmitResult>(API.submitPayment, params, config);
    }

    applyExclusiveAccount(authCode: string, config?: HttpConfig) {
        return this.post(API.applyExclusiveAccount, {authCode}, config);
    }

    createPaymentProcess(params: CompRechargeCreateParams, config?: HttpConfig) {
        return this.post(API.createPaymentProcess, params, config);
    }

    getAccountTelephone(config?: HttpConfig) {
        return this.post<{
            mobile: string;
        }>(API.getAccountTelephone, null, config);
    }

    financeSendAuthCode(config?: HttpConfig) {
        return this.get(API.financeSendAuthCode, null, config);
    }

    personToken(config?: HttpConfig) {
        return this.post<FaceToken>(API.personToken, {}, config);
    }

    legalToken(config?: HttpConfig) {
        return this.post<FaceToken>(API.legalToken, null, config);
    }

    authorizedToken(config?: HttpConfig) {
        return this.post<FaceToken>(API.authorizedToken, {}, config);
    }

    getShortUrl(params: GetShortUrlParams, config?: HttpConfig) {
        return this.post<string>(API.getShortUrl, params, config);
    }

    changeLog(config?: HttpConfig) {
        return this.get<{
            result: ChangeLog[];
            total: number;
        }>(API.changeLog, null, config);
    }

    queryAllAuthRecord(config?: HttpConfig) {
        return this.get<AuthRecordItem[]>(API.queryAllAuthRecord, null, config);
    }

    drawbackChange(config?: HttpConfig) {
        return this.post<FaceToken>(API.drawbackChange, {}, config);
    }

    licenseInfo(params: LicenseInfoParams, config?: HttpConfig) {
        return this.post<LicenseInfo>(API.licenseInfo, params, config);
    }

    licenseUpdate(params: LicenseUpdateParams, config?: HttpConfig) {
        return this.post(API.licenseUpdate, params, config);
    }

    licenseApply(params: LicenseUpdateParams, config?: HttpConfig) {
        return this.post<{uuid?: string}>(API.licenseApply, params, config);
    }

    licenseDelete(uuid: string, config?: HttpConfig) {
        return this.post(API.licenseDelete, {uuid}, config);
    }

    birthday(config?: HttpConfig) {
        return this.post<string>(API.birthday, {}, config);
    }

    syncPass(
        params: {
            extraInfo?: string;
            isActivated?: number;
        },
        config?: HttpConfig
    ) {
        return this.post<string>(API.syncPass, params, config);
    }

    queryPersonCertificate(params: PersonDocQueryParams, config?: HttpConfig) {
        return this.post<PersonDocQueryResult>(API.queryPersonCertificate, params, config);
    }

    submitPersonCertificate(params: PersonDocSubmit, config?: HttpConfig) {
        return this.post(API.submitPersonCertificate, params, config);
    }

    qualifyWithdraw(pipelineType: PipelineType, config?: HttpConfig) {
        return this.post(API.qualifyWithdraw, {pipelineType}, config);
    }

    faceLog(params: FaceLog, config?: HttpConfig) {
        return this.post(API.faceLog, params, config);
    }

    getCompLatestInfo(config?: HttpConfig) {
        return this.get<CompLicenseQueryResult>(API.getCompLatestInfo, undefined, config);
    }

    submitCompChange(params: CompRegisterParams, config?: HttpConfig) {
        return this.post<CompRegisterResult>(API.submitCompChange, params, config);
    }

    twoFactorCheck(params: CompTwoFactorParams, config?: HttpConfig) {
        return this.post<CompTwoFactorResult>(API.twoFactorCheck, params, config);
    }

    getCompChangeInfo(config?: HttpConfig) {
        return this.post<CompChangeQuery>(API.getCompChangeInfo, undefined, config);
    }

    /**
     * 获取常量V3,iframe等场景下不加载fe-framework但是仍需要获取常量userid
     *
     * @param config HttpConfig对象，可选
     * @returns 返回获取到的常量V3
     */
    constantV3(locale?: string, config?: HttpConfig) {
        return this.post<any>(API.constantV3, {locale: locale || 'zh-cn'}, config);
    }

    compChangeCheck(config?: HttpConfig) {
        return this.post<CompanyChangeCheck>(API.compChangeCheck, undefined, config);
    }

    compChangeCheckOnly(config?: HttpConfig) {
        return this.post<CompanyChangeCheck>(API.compChangeCheckOnly, undefined, config);
    }

    compChangeOriginQuery(config?: HttpConfig) {
        return this.post<CompanyChangeOriginResult>(API.compChangeOriginQuery, config);
    }

    compChangeOriginSubmit(params: CompChangeOriginSubmitParams, config?: HttpConfig) {
        return this.post(API.compChangeOriginSubmit, params, config);
    }

    compChangeOriginPersonFace(params: CompChangeOriginPersonSubmit, config?: HttpConfig) {
        return this.post<CompChangeOriginPersonResult>(API.compChangeOriginPersonFace, params, config);
    }

    compFileOriginSubmit(applyForm: string, config?: HttpConfig) {
        return this.post(API.compFileOriginSubmit, {applyForm}, config);
    }

    compChangeFaceToken(config?: HttpConfig) {
        return this.post<{authToken: string}>(API.compChangeFaceToken, undefined, config);
    }

    getCompChangeForm(config?: HttpConfig) {
        return this.post<{applyForm: string}>(API.getCompChangeForm, undefined, config).then(res => {
            return res?.applyForm ? getImageInfo(res.applyForm) : '';
        });
    }

    /**
     * 记录是否勾选变更协议
     * 变更协议版本修改后，和后端约定新的版本号version
     * 前端设置新的协议版本的链接，后端调整版本号对应协议链接
     * @param params 请求参数
     * @param config 请求配置
     * @returns 返回请求结果
     */
    compChangeAgreement(params: {
        type: 'origin' | 'new';
        version: 'v1';
    }, config?: HttpConfig) {
        return this.post<{applyForm: string}>(API.compChangeAgreement, params, config);
    }

    checkCompInRestrict(corporationName: string, config?: HttpConfig) {
        return this.post<boolean>(API.checkCompInRestrict, {corporationName}, config);
    }

    generateAuthorization(params: {personName: string, personNumber: string}, config?: HttpConfig) {
        return this.post<{fileName: string}>(API.generateAuthorization, params, config).then(res => {
            return res?.fileName ? getImageInfo(res.fileName) : '';
        });
    }
}

export default new QualifyClient(QualifyHttpConfig);
